/**
 * YouTube Data API v3 Service
 *
 * Requires environment variable: VITE_YOUTUBE_API_KEY
 * Get your API key from: https://console.cloud.google.com/apis/credentials
 */

export interface YouTubeVideo {
  id: string
  title: string
  description: string
  thumbnailUrl: string
  publishedAt: string
  videoUrl: string
}

export type YouTubeLiveBroadcastContent = 'live' | 'upcoming' | 'none';

export type YouTubeStreamStatus = 'live' | 'vod' | 'error';

export interface YouTubeVideoLiveStatus {
  isLive: boolean;
  broadcastContent: YouTubeLiveBroadcastContent;
  status: YouTubeStreamStatus;
}

interface YouTubeVideoAPIResponse {
  items?: Array<{
    snippet: {
      liveBroadcastContent: string;
    };
  }>;
}

interface YouTubeAPIResponse {
  items: Array<{
    id: {
      videoId: string
    }
    snippet: {
      title: string
      description: string
      publishedAt: string
      thumbnails: {
        medium: {
          url: string
        }
        high?: {
          url: string
        }
      }
    }
  }>
}

/**
 * Fetch latest videos from a YouTube channel
 * @param channelId YouTube channel ID
 * @param maxResults Maximum number of videos to fetch (default: 8)
 * @returns Array of YouTube videos
 */
export async function fetchChannelVideos(
  channelId: string,
  maxResults: number = 8
): Promise<YouTubeVideo[]> {
  const apiKey = import.meta.env.VITE_YOUTUBE_API_KEY

  if (apiKey === undefined || apiKey === '') {
    console.error('YouTube API key not configured in environment variables')
    throw new Error('YouTube API key not configured')
  }

  const url = new URL('https://www.googleapis.com/youtube/v3/search')
  url.searchParams.append('key', apiKey)
  url.searchParams.append('channelId', channelId)
  url.searchParams.append('part', 'snippet')
  url.searchParams.append('order', 'date')
  url.searchParams.append('maxResults', maxResults.toString())
  url.searchParams.append('type', 'video')

  try {
    const response = await fetch(url.toString())

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('YouTube API error:', response.status, errorData)
      throw new Error(`YouTube API error: ${response.status}`)
    }

    const data: YouTubeAPIResponse = await response.json()

    return data.items.map((item) => ({
      id: item.id.videoId,
      title: item.snippet.title,
      description: item.snippet.description,
      thumbnailUrl: item.snippet.thumbnails.high?.url || item.snippet.thumbnails.medium.url,
      publishedAt: item.snippet.publishedAt,
      videoUrl: `https://www.youtube.com/watch?v=${item.id.videoId}`
    }))
  } catch (error) {
    console.error('Error fetching YouTube videos:', error)
    throw error
  }
}

/**
 * Check if a YouTube video is currently live streaming.
 * Returns a safe default on error (never throws).
 * @param videoId 11-character YouTube video ID
 */
export async function checkVideoLiveStatus(videoId: string): Promise<YouTubeVideoLiveStatus> {
  const errorStatus: YouTubeVideoLiveStatus = { isLive: false, broadcastContent: 'none', status: 'error' };

  const apiKey = import.meta.env.VITE_YOUTUBE_API_KEY;
  if (apiKey === undefined || apiKey === '') {
    console.error('YouTube API key not configured in environment variables');
    return errorStatus;
  }

  try {
    const url = new URL('https://www.googleapis.com/youtube/v3/videos');
    url.searchParams.append('key', apiKey);
    url.searchParams.append('id', videoId);
    url.searchParams.append('part', 'snippet');

    const response = await fetch(url.toString());
    if (!response.ok) {
      console.error('YouTube API error:', response.status);
      return errorStatus;
    }

    const data: YouTubeVideoAPIResponse = await response.json();
    const item = data.items?.[0];

    if (item === undefined) {
      console.error('YouTube video not found:', videoId);
      return errorStatus;
    }

    const broadcastContent = item.snippet?.liveBroadcastContent;

    if (broadcastContent === 'live') {
      return { isLive: true, broadcastContent: 'live', status: 'live' };
    }

    if (broadcastContent === 'upcoming') {
      return { isLive: false, broadcastContent: 'upcoming', status: 'vod' };
    }

    return { isLive: false, broadcastContent: 'none', status: 'vod' };
  } catch (error) {
    console.error('Error checking YouTube live status:', error);
    return errorStatus;
  }
}
